import { ReactNode } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { SurfaceHeader } from '@frontend/surface-header';
import { theme } from '@frontend/theme';
import { Button, ModalControlModalProps, Tray } from '@frontend/design-system';

export interface AnalyticsFilterTrayProps extends ModalControlModalProps {
  /** Title shown in the tray header. */
  title: string;
  /** Filter sections rendered inside the tray body. */
  children: ReactNode;
  /** Called when the user clicks "Apply Filters". */
  onApply: () => void;
  /** Called when the user clicks "Clear All Filters". */
  onClearAll: () => void;
  /** Optional tracking IDs for the action buttons. */
  trackingIds?: {
    apply?: string;
    cancel?: string;
    clearAll?: string;
  };
  /** Override labels for the action buttons. */
  labels?: {
    apply?: string;
    cancel?: string;
    clearAll?: string;
  };
}

/**
 * Reusable filter tray shell for analytics features.
 *
 * Provides a consistent layout with:
 * - A `SurfaceHeader` with a title and close button
 * - A scrollable `Tray.Body` for filter sections
 * - A footer with "Clear All Filters", "Cancel", and "Apply Filters" actions
 *
 * Consumers are responsible for managing draft filter state and passing
 * filter section components as `children`.
 *
 * @example
 * ```tsx
 * <AnalyticsFilterTray
 *   {...modalProps}
 *   title="Phone Analytics Filters"
 *   onApply={() => { commitFilters(); modalProps.onClose(); }}
 *   onClearAll={() => resetDraftFilters()}
 * >
 *   <MyFilterSection value={draft.status} onChange={...} />
 *   <AnotherFilterSection value={draft.duration} onChange={...} />
 * </AnalyticsFilterTray>
 * ```
 */
export const AnalyticsFilterTray = ({
  title,
  children,
  onApply,
  onClearAll,
  trackingIds: trackIds,
  labels,
  ...trayProps
}: AnalyticsFilterTrayProps) => {
  const { t } = useTranslation('analytics');

  const applyLabel = labels?.apply ?? t('Apply Filters');
  const cancelLabel = labels?.cancel ?? t('Cancel');
  const clearAllLabel = labels?.clearAll ?? t('Clear All Filters');

  return (
    <Tray {...trayProps} css={styles.tray} width='medium'>
      <SurfaceHeader.Root>
        <SurfaceHeader.Row>
          <SurfaceHeader.Title>{title}</SurfaceHeader.Title>
          <SurfaceHeader.Actions>
            <SurfaceHeader.Icon iconName='x' onClick={trayProps.onClose} />
          </SurfaceHeader.Actions>
        </SurfaceHeader.Row>
      </SurfaceHeader.Root>

      <Tray.Body css={styles.body}>{children}</Tray.Body>

      <div css={styles.actions}>
        <Button variant='tertiary' onClick={onClearAll} css={{ marginRight: 'auto' }} trackingId={trackIds?.clearAll}>
          {clearAllLabel}
        </Button>
        <Button variant='secondary' onClick={trayProps.onClose} trackingId={trackIds?.cancel}>
          {cancelLabel}
        </Button>
        <Button variant='primary' onClick={onApply} trackingId={trackIds?.apply}>
          {applyLabel}
        </Button>
      </div>
    </Tray>
  );
};

const styles = {
  tray: css`
    padding: 0;
    @media (max-width: ${breakpoints.xsmall.max}px) {
      width: 100%;
    }
  `,
  body: css`
    padding: ${theme.spacing(0, 4)};
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
  `,
  actions: css`
    display: flex;
    gap: ${theme.spacing(2)};
    align-items: center;
    justify-content: flex-end;
    padding: ${theme.spacing(2, 4)};
    border-top: 1px solid ${theme.colors.neutral10};
  `,
};
